// apps/web/src/pages/Profile/components/OrganizationSection.tsx

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useMyOrganization, useUpdateMyOrganization } from '@alamo/api';
import type {
  MyOrganization,
  MyOrganizationTaxRegime,
  UpdateMyOrganizationInput,
} from '@alamo/types';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
} from '@alamo/ui';
import { extractApiError, maskCNPJ } from '@alamo/utils';

import { nameSchema } from '../../../lib/nameSchema';

const TAX_REGIMES: MyOrganizationTaxRegime[] = [
  'simples_nacional',
  'lucro_presumido',
  'lucro_real',
  'mei',
];

// Sentinela para "sem regime" — Radix Select não aceita item com value="".
const TAX_REGIME_NONE = 'not_informed';

export const organizationSchema = z.object({
  tradeName: nameSchema().optional(),
  taxRegime: z.enum([TAX_REGIME_NONE, 'simples_nacional', 'lucro_presumido', 'lucro_real', 'mei']),
  stateRegistration: z.string().max(50).optional(),
  municipalRegistration: z.string().max(50).optional(),
});

type OrganizationFormValues = z.infer<typeof organizationSchema>;

/**
 * Seção "Dados da empresa" do perfil self-service (consultor PJ).
 *
 * PF/CLT conhecidos não disparam a consulta. Para os demais perfis, o gate de
 * visibilidade continua sendo GET /profile/organization retornar 200.
 */
export function OrganizationSection({ enabled = true }: { enabled?: boolean }) {
  const { data: organization } = useMyOrganization({ enabled });

  if (!organization) return null;

  return <OrganizationForm organization={organization} />;
}

function OrganizationForm({ organization }: { organization: MyOrganization }) {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const update = useUpdateMyOrganization();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationSchema),
    values: {
      tradeName: organization.tradeName ?? '',
      taxRegime: organization.taxRegime ?? TAX_REGIME_NONE,
      stateRegistration: organization.stateRegistration ?? '',
      municipalRegistration: organization.municipalRegistration ?? '',
    },
  });

  // cnpjLast4 = last 4 DIGITS of the CNPJ, which map to the last two positions
  // O DONO vê o próprio CNPJ completo (decisão Pedro 2026-07-09) — formatado;
  // fallback pro last-4 mascarado só em payload antigo sem o campo.
  const displayCnpj = organization.cnpj
    ? maskCNPJ(organization.cnpj)
    : organization.cnpjLast4
      ? `**.***.***/****-${organization.cnpjLast4}`
      : '—';

  const onSubmit = (values: OrganizationFormValues) => {
    const payload: UpdateMyOrganizationInput = {
      tradeName: values.tradeName || undefined,
      stateRegistration: values.stateRegistration || undefined,
      municipalRegistration: values.municipalRegistration || undefined,
    };
    if (values.taxRegime !== TAX_REGIME_NONE) {
      payload.taxRegime = values.taxRegime;
    }

    update.mutate(payload, {
      onSuccess: () => {
        toast.success(t('auth:profile.organization.toast.updated'));
      },
      onError: (err: unknown) => {
        const { code, message } = extractApiError(err);
        toast.error(
          t(`errors:${code ?? 'generic'}`, {
            defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
          }),
        );
      },
    });
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-heading font-bold text-secondary-950 mb-4">
        {t('auth:profile.sections.organization')}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label={t('auth:profile.organization.fields.cnpj')}
          value={displayCnpj}
          disabled
          readOnly
          helperText={t('auth:profile.organization.helpers.cnpj_readonly')}
        />
        <Input
          label={t('auth:profile.organization.fields.legal_name')}
          value={organization.legalName}
          disabled
          readOnly
          helperText={t('auth:profile.organization.helpers.legal_name_readonly')}
        />
        <Input
          label={t('auth:profile.organization.fields.trade_name')}
          {...register('tradeName')}
          error={errors.tradeName ? t(errors.tradeName.message as string) : undefined}
        />
        <div className="space-y-1">
          <label
            htmlFor="profile-org-tax-regime"
            className="text-sm font-body font-medium text-slate-600"
          >
            {t('auth:profile.organization.fields.tax_regime')}
          </label>
          <Controller
            control={control}
            name="taxRegime"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="profile-org-tax-regime" className="w-full min-h-[44px]">
                  {t(`auth:profile.organization.tax_regime_options.${field.value}`)}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TAX_REGIME_NONE}>
                    {t('auth:profile.organization.tax_regime_options.not_informed')}
                  </SelectItem>
                  {TAX_REGIMES.map((regime) => (
                    <SelectItem key={regime} value={regime}>
                      {t(`auth:profile.organization.tax_regime_options.${regime}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={t('auth:profile.organization.fields.state_registration')}
            {...register('stateRegistration')}
            error={
              errors.stateRegistration ? t(errors.stateRegistration.message as string) : undefined
            }
          />
          <Input
            label={t('auth:profile.organization.fields.municipal_registration')}
            {...register('municipalRegistration')}
            error={
              errors.municipalRegistration
                ? t(errors.municipalRegistration.message as string)
                : undefined
            }
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            className="min-h-[44px]"
            disabled={!isDirty || update.isPending}
            aria-busy={update.isPending || undefined}
          >
            {update.isPending && <Spinner size="sm" />}
            {t('auth:profile.organization.actions.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
